// src/pages/departments/ComputerEngineering.js
import { motion } from "framer-motion";

const ComputerEngineering = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center">
        <img
          src="/images/department-hero.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center text-white px-6">
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-3"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Department of Computer Engineering
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Excellence in Teaching, Research, and Innovation
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-gray-600 leading-relaxed">
            The Department of Computer Engineering was established in [Year] with the
            vision of fostering innovation and excellence. Over the years, it
            has grown into a hub of academic research, practical learning, and
            community impact.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <img
              src="/images/hod-profile.jpg"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Dear students and stakeholders, it is my pleasure to welcome you
              to the Department of Computer Engineering. Our mission is to nurture
              talent, inspire creativity, and empower students with the skills
              and values needed to thrive in today’s dynamic world.
            </p>
            <p className="mt-4 text-blue-700 font-semibold">— Dr. John Doe</p>
            <p className="text-gray-500">Head of Department</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To be a leading center of excellence in teaching, research, and
              innovation, producing graduates who are globally competitive and
              socially responsible.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              To provide high-quality education and training that equips
              students with practical skills, ethical values, and leadership
              qualities for professional and societal development.
            </p>
          </div>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["gallery1.jpg", "gallery2.jpg", "gallery3.jpg", "gallery4.jpg"].map(
              (img, i) => (
                <img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md hover:scale-105 transition-transform"
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State,
            Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.departmentname@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default ComputerEngineering;
